import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import { useAuth } from '../../components/navigation/AuthenticationGuard';
import { postsService } from '../../services/postsService';
import PostsTable from './components/PostsTable';
import PostsCardList from './components/PostsCardList';
import FilterControls from './components/FilterControls';
import BulkActions from './components/BulkActions';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import EmptyState from './components/EmptyState';

const AdminPostsManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    author: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState({
    column: 'updatedAt',
    direction: 'desc'
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [postsToDelete, setPostsToDelete] = useState([]);

  const authors = useMemo(() => {
    return [...new Set(posts.map((post) => post.author))];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts?.filter(post => {
      const matchesSearch = post?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                           post?.excerpt?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesStatus = filters?.status === 'all' || post?.status === filters?.status;
      return matchesSearch && matchesStatus;
    });
  }, [posts, filters]);

  const filteredAndSortedPosts = useMemo(() => {
    let filtered = [...filteredPosts];

    if (filters?.dateFrom) {
      filtered = filtered?.filter((post) =>
      new Date(post.createdAt) >= new Date(filters.dateFrom)
      );
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter((post) =>
      new Date(post.createdAt) <= new Date(filters.dateTo)
      );
    }

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'createdAt' || sortConfig?.column === 'updatedAt') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [filteredPosts, filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      author: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectPost = (postId, checked) => {
    setSelectedPosts((prev) =>
    checked ?
    [...prev, postId] :
    prev?.filter((id) => id !== postId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedPosts(checked ? filteredAndSortedPosts?.map((post) => post?.id) : []);
  };

  const handleEdit = (postId) => {
    navigate(`/admin/posts/${postId}`);
  };

  const handlePreview = (postId) => {
    navigate(`/admin/posts/preview/${postId}`);
  };

  const handleToggleStatus = async (postId) => {
    const post = posts?.find(p => p?.id === postId);
    if (!post) return;

    try {
      const newStatus = post?.status === 'published' ? 'draft' : 'published';
      await postsService?.updatePost(postId, {
        ...post,
        status: newStatus,
        publishedAt: newStatus === 'published' ? new Date()?.toISOString() : null
      });
      
      setPosts((prev) => prev?.map((p) =>
        p?.id === postId ?
        {
          ...p,
          status: newStatus,
          updatedAt: new Date()?.toISOString()
        } :
        p
      ));
    } catch (err) {
      setError(err?.message || 'Failed to update post status');
      console.error('Toggle status error:', err);
    }
  };

  const handleBulkPublish = () => {
    setPosts((prev) => prev?.map((post) =>
    selectedPosts?.includes(post?.id) ?
    { ...post, status: 'published', updatedAt: new Date()?.toISOString() } :
    post
    ));
    setSelectedPosts([]);
  };

  const handleBulkUnpublish = () => {
    setPosts((prev) => prev?.map((post) =>
    selectedPosts?.includes(post?.id) ?
    { ...post, status: 'draft', updatedAt: new Date()?.toISOString() } :
    post
    ));
    setSelectedPosts([]);
  };

  const handleDeleteClick = (postId) => {
    setPostsToDelete([postId]);
    setDeleteModalOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setPostsToDelete(selectedPosts);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    setPosts((prev) => prev?.filter((post) => !postsToDelete?.includes(post?.id)));
    setSelectedPosts([]);
    setDeleteModalOpen(false);
    setPostsToDelete([]);
  };

  const handleCreatePost = () => {
    navigate('/admin/posts/new');
  };

  const hasActiveFilters =
  filters?.status !== 'all' ||
  filters?.author !== 'all' ||
  filters?.dateFrom ||
  filters?.dateTo ||
  filters?.search;

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await postsService?.getAllPosts();
      setPosts(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load posts');
      console.error('Load posts error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await postsService?.deletePost(postId);
      setPosts(prev => prev?.filter(p => p?.id !== postId));
      setSelectedPosts(prev => prev?.filter(id => id !== postId));
    } catch (err) {
      setError(err?.message || 'Failed to delete post');
      console.error('Delete post error:', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPosts?.length === 0) return;
    setDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await postsService?.deletePosts(selectedPosts);
      setPosts(prev => prev?.filter(p => !selectedPosts?.includes(p?.id)));
      setSelectedPosts([]);
      setDeleteModalOpen(false);
    } catch (err) {
      setError(err?.message || 'Failed to delete posts');
      console.error('Bulk delete error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading posts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadPosts}>Retry</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="content-area">
        <div className="max-w-7xl mx-auto py-6 md:py-8">
          <BreadcrumbNavigation />
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                Posts Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage your blog content, publish posts, and control visibility
              </p>
            </div>
            <Button
              variant="default"
              onClick={handleCreatePost}
              iconName="Plus"
              iconPosition="left"
              iconSize={18}>

              Create New Post
            </Button>
          </div>

          <div className="space-y-4 md:space-y-6">
            <FilterControls
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              authors={authors}
              totalCount={posts?.length}
              filteredCount={filteredAndSortedPosts?.length} />


            {selectedPosts?.length > 0 &&
            <BulkActions
              selectedCount={selectedPosts?.length}
              onPublish={handleBulkPublish}
              onUnpublish={handleBulkUnpublish}
              onDelete={handleBulkDeleteClick} />

            }

            {filteredAndSortedPosts?.length === 0 ?
            <EmptyState
              hasFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              onCreatePost={handleCreatePost} /> :


            <>
                <PostsTable
                posts={filteredAndSortedPosts}
                selectedPosts={selectedPosts}
                onSelectPost={handleSelectPost}
                onSelectAll={handleSelectAll}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteClick}
                sortConfig={sortConfig}
                onSort={handleSort} />

                
                <PostsCardList
                posts={filteredAndSortedPosts}
                selectedPosts={selectedPosts}
                onSelectPost={handleSelectPost}
                onEdit={handleEdit}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteClick} />

              </>
            }
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        postCount={postsToDelete?.length} />

    </div>);

};

export default AdminPostsManagement;